import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType, TripStatus, TripType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Trip } from "@mapmycustomers/shared/types/entity";
import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRangeType from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  getDistanceTravelledDrillDownConfiguration,
  getDistanceTravelledDrillDownCustomDateRange,
  getDistanceTravelledDrillDownScope,
  getDistanceTravelledDrillDownTotalFilteredRecords,
  getDistanceTravelledDrillDownViewState,
} from "@app/scene/dashboard/store/cards/distanceTravelled/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { callApi } from "@app/store/api/callApi";
import { fetchDistanceTravelledCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { buildDistanceFilters } from "@app/store/dashboard/distanceTravelled/fetchFromDistances";
import resolveScopedUserIds from "@app/store/dashboard/distanceTravelled/resolveScopedUserIds";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import {
  doesOrganizationSupportMileageTracking,
  getCurrentUserId,
  getOrganizationId,
} from "@app/store/iam";
import { getAllUsersByIdOfEntireOrganization, getTeams } from "@app/store/members";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import DistanceTravelledCardConfiguration from "@app/types/dashboard/cards/distanceTravelled/DistanceTravelledCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import distanceFieldModel, { DistanceFieldName } from "@app/util/fieldModel/DistanceFieldModel";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyDistanceTravelledDrillDownListViewSettings,
  exportDistanceTravelledCardDrillDownData,
  fetchDistanceTravelledCardData,
  fetchDistanceTravelledCardDrillDownData,
  showDistanceTravelledDrillDown,
  updateDistanceTravelledTrip,
} from "./actions";

const defaultColumns: string[] = [
  TripFieldName.START_TIME,
  TripFieldName.USER,
  TripFieldName.START,
  TripFieldName.FINISH,
  TripFieldName.RATE,
  TripFieldName.DISTANCE,
  TripFieldName.DURATION,
  TripFieldName.BUSINESS_COST,
];

const defaultSortColumn = TripFieldName.START_TIME;

/** Separate keys because the two branches show different columns off different field models. */
const VIEW_SETTINGS_KEY = "dashboard/distanceTravelled";
const ESTIMATED_VIEW_SETTINGS_KEY = "dashboard/distanceTravelledEstimated";

function* onFetchDistanceTravelledCardData({
  payload,
}: ReturnType<typeof fetchDistanceTravelledCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;
  try {
    yield call(fetchDistanceTravelledCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

/**
 * Columns shown by default in the location-distance breakdown. Without Mileage Tracker there are no
 * trips, so totalDistance already equals passiveDistance; both are opt-in to avoid a duplicate.
 */
const distanceColumns: string[] = [
  DistanceFieldName.USER,
  DistanceFieldName.DATE,
  DistanceFieldName.TOTAL_DISTANCE,
];

// Without Mileage Tracker there are no trips to list, so View Data shows the per user-day location
// rows the estimate was built from.
function* onOpenEstimatedDrillDown(): Generator<unknown, void, never> {
  const drillDownViewState: DrillDownViewState = yield select(
    getDistanceTravelledDrillDownViewState
  );

  const viewState = localSettings.getViewSettings(ESTIMATED_VIEW_SETTINGS_KEY, distanceFieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(distanceColumns, distanceFieldModel),
    sort: [{ field: distanceFieldModel.getByName(DistanceFieldName.DATE)!, order: SortOrder.DESC }],
  });

  // The range and user scoping are rebuilt from the stored scope at fetch time, because the
  // distances endpoint is filtered by date/userId directly rather than through a FilterModel.
  viewState.filter = createEmptyFilterModel();
  yield put(showDistanceTravelledDrillDown.success({ viewState }));
}

function* onOpenDrillDown({
  payload,
}: ReturnType<typeof showDistanceTravelledDrillDown.request>): Generator<unknown, void, never> {
  const { configuration, customDateRange, scope } = payload;
  const { dateRange } = configuration;

  const supportsMileageTracking: boolean = yield select(doesOrganizationSupportMileageTracking);
  if (!supportsMileageTracking) {
    yield* onOpenEstimatedDrillDown();
    return;
  }

  const drillDownViewState: DrillDownViewState = yield select(
    getDistanceTravelledDrillDownViewState
  );

  const viewState = localSettings.getViewSettings(VIEW_SETTINGS_KEY, tripFieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(defaultColumns, tripFieldModel),
    sort: [
      {
        field: tripFieldModel.getByName(defaultSortColumn)!,
        order: SortOrder.DESC,
      },
    ],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const isUserScope = !scope?.teamId && !!scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(TripFieldName.START_TIME, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);

  const teams: Team[] = yield select(getTeams);
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(
        CommonFieldName.TEAM,
        FilterOperator.IN_ANY,
        teams.find(({ id }) => id === scope.teamId)?.users.map(({ id }) => id) ?? []
      )
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }

  const currentUserId: User["id"] = yield select(getCurrentUserId);
  // when fetching for other users, only fetch business trips
  if (!isUserScope || scope?.userId !== currentUserId) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.TYPE, FilterOperator.EQUALS, TripType.BUSINESS)
    );
  }

  viewState.filter = filter;
  yield put(showDistanceTravelledDrillDown.success({ viewState }));
}

// Rows carry only userId, so the user is attached here for the grid's user chip.
function* fetchEstimatedDrillDownPage(params: {
  $limit: number;
  $offset: number;
  drillDownViewState: DrillDownViewState;
  orgId: Organization["id"];
  payload: ReturnType<typeof fetchDistanceTravelledCardDrillDownData.request>["payload"];
}): Generator<unknown, void, never> {
  const { $limit, $offset, drillDownViewState, orgId, payload } = params;

  // the drill-down only fetches while it is open, so its configuration is set by now
  const configuration: DistanceTravelledCardConfiguration = yield select(
    getDistanceTravelledDrillDownConfiguration
  );
  const customDateRange: DateRangeType | undefined = yield select(
    getDistanceTravelledDrillDownCustomDateRange
  );
  const scope: Scope | undefined = yield select(getDistanceTravelledDrillDownScope);
  const { userIds } = yield* resolveScopedUserIds(scope);

  const range =
    customDateRange ??
    getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange);

  const response: ListResponse<DistanceRecord> = yield callApi("fetchDistance", orgId, {
    $filters: buildDistanceFilters(range, userIds),
    $limit,
    $offset,
    $order: convertToPlatformSortModel(drillDownViewState.sort),
  });

  const usersById: Map<User["id"], User> = yield select(getAllUsersByIdOfEntireOrganization);
  const data = response.data.map((row) => ({ ...row, user: usersById.get(row.userId) }));

  if (payload.dataCallback) {
    yield call(payload.dataCallback, { ...response, data });
  }
  yield put(
    fetchDistanceTravelledCardDrillDownData.success({
      totalFilteredRecords: response.total,
      totalRecords: response.accessible ?? response.total,
    })
  );
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchDistanceTravelledCardDrillDownData.request>): Generator<
  unknown,
  void,
  never
> {
  try {
    loggingService.debug("Dashboard: activities logged card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyDistanceTravelledDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(
      getDistanceTravelledDrillDownViewState
    );

    const supportsMileageTracking: boolean = yield select(doesOrganizationSupportMileageTracking);

    if (!payload.fetchOnlyWithoutFilters) {
      // The two branches read different keys, so writing one key would lose the other's columns.
      localSettings.setViewSettings(
        drillDownViewState,
        supportsMileageTracking ? VIEW_SETTINGS_KEY : ESTIMATED_VIEW_SETTINGS_KEY
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    if (!supportsMileageTracking) {
      yield* fetchEstimatedDrillDownPage({ $limit, $offset, drillDownViewState, orgId, payload });
      return;
    }

    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        status: TripStatus.COMPLETED,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          tripFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Trip> = yield callApi("fetchTrips", orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchDistanceTravelledCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchDistanceTravelledCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport(): Generator<unknown, void, never> {
  const drillDownViewState: DrillDownViewState = yield select(
    getDistanceTravelledDrillDownViewState
  );
  const total: number = yield select(getDistanceTravelledDrillDownTotalFilteredRecords);

  const supportsMileageTracking: boolean = yield select(doesOrganizationSupportMileageTracking);
  if (!supportsMileageTracking) {
    // The grid's FilterModel carries no date or user scoping for these rows, so rebuild it here
    // exactly as the fetch does, otherwise the CSV covers every distance row in the org.
    const configuration: DistanceTravelledCardConfiguration = yield select(
      getDistanceTravelledDrillDownConfiguration
    );
    const customDateRange: DateRangeType | undefined = yield select(
      getDistanceTravelledDrillDownCustomDateRange
    );
    const scope: Scope | undefined = yield select(getDistanceTravelledDrillDownScope);
    const { userIds } = yield* resolveScopedUserIds(scope);
    const range =
      customDateRange ??
      getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange);

    yield put(
      exportEntities.request({
        entityType: EntityType.DISTANCE,
        platformRequest: { $filters: buildDistanceFilters(range, userIds) },
        total,
        viewState: drillDownViewState,
      })
    );
    return;
  }

  yield put(
    exportEntities.request({
      entityType: EntityType.TRIP,
      total,
      viewState: drillDownViewState,
    })
  );
}

function* onUpdateTrip({ payload }: ReturnType<typeof updateDistanceTravelledTrip.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedTrip: Trip = yield callApi("updateTrip", orgId, payload.entity);
    if (payload.onSuccess) {
      yield call(payload.onSuccess, updatedTrip);
    }
    yield put(updateDistanceTravelledTrip.success(updatedTrip));
    yield put(notifyAboutChanges({ entityType: EntityType.TRIP, updated: [updatedTrip] }));
  } catch (error) {
    if (payload.onFailure) {
      yield call(payload.onFailure);
    }
    yield put(updateDistanceTravelledTrip.failure(error));
    yield put(handleError({ error }));
  }
}

export function* distanceTravelledSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchDistanceTravelledCardData, onFetchDistanceTravelledCardData);
  // takeEvery for the same reason as the card data above: a board can hold several of these cards
  yield takeLatest(showDistanceTravelledDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchDistanceTravelledCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportDistanceTravelledCardDrillDownData, onExport);
  yield takeLatest(exportDistanceTravelledCardDrillDownData, onExport);
  yield takeEvery(updateDistanceTravelledTrip.request, onUpdateTrip);
}
